import type { Api } from "../api";
import { empty, h, label, pill } from "../dom";
import { money, qty, shortDate, today, unitPrice } from "../format";
import { discountWords, dueWords, failedChecks, invoiceOrder, invoiceStatus } from "../invoices";
import { navigate } from "../nav";
import { viewMode } from "../orders";
import type { Mode, SupplierInvoice } from "../types";
import { plain } from "../words";
import { modeNotice } from "./shared";

// Invoices (invoice_status, invoices.read): supplier invoices, how each matched its vendor PO (price exact to 3
// decimals, quantity within 10%, extended within $1) and which check failed, when it's due and any early-pay discount.
// Read only: paying stays with Josh and Tela, and bills in QuickBooks are phase 5. Open ones by default.

interface Answer { mode: Mode; showing?: Mode; supplierInvoices: SupplierInvoice[]; next_cursor: string | null }

export async function invoices(api: Api, query: URLSearchParams): Promise<HTMLElement> {
  const party = (query.get("supplier") ?? "").slice(0, 100);
  const show = ["OPEN", "PAST_DUE", "ALL"].includes(query.get("show") ?? "") ? query.get("show")! : "OPEN";
  const asked = ["LIVE", "SIMULATION"].includes(query.get("mode") ?? "") ? query.get("mode")! : undefined;
  const r = await api.tool<Answer>("invoice_status", {
    kind: "SUPPLIER", limit: 200, open_only: show !== "ALL", ...(show === "PAST_DUE" ? { past_due: true } : {}),
    ...(party ? { party } : {}), ...(asked ? { mode: asked } : {}),
  });
  const view = viewMode(query, r.mode);
  const now = today();
  const rows = invoiceOrder(r.supplierInvoices ?? []);
  const unmatched = rows.filter((i) => i.status === "EXCEPTION").length;
  const pastDue = rows.filter((i) => dueWords(i, now)?.tone === "late").length;

  const supplierInput = h("input", { id: "supplier", type: "search", value: party, placeholder: "Rusken, Cascades…", maxlength: 100 });
  const showSelect = h("select", { id: "show" }, [["OPEN", "Open"], ["PAST_DUE", "Past due"], ["ALL", "All, paid too"]].map(([v, t]) =>
    h("option", { value: v, selected: v === show }, t)));
  const modeSelect = h("select", { id: "mode" }, (["LIVE", "SIMULATION"] as const).map((m) =>
    h("option", { value: m, selected: m === view.mode }, `${m === "LIVE" ? "Live" : "Simulation copies"}${m === r.mode ? " (the platform's mode)" : ""}`)));
  const go = () => navigate(`/invoices?${new URLSearchParams({
    ...(supplierInput.value.trim() ? { supplier: supplierInput.value.trim() } : {}), show: showSelect.value,
    ...(modeSelect.value !== r.mode ? { mode: modeSelect.value } : {}) })}`);
  const form = h("form", { class: "filters", role: "search" },
    h("div", { class: "field" }, h("label", { for: "supplier" }, "Supplier"), supplierInput),
    h("div", { class: "field" }, h("label", { for: "show" }, "Invoices"), showSelect),
    h("div", { class: "field" }, h("label", { for: "mode" }, "Showing"), modeSelect),
    h("button", { type: "submit", class: "button" }, "Show"));
  form.addEventListener("submit", (e) => { e.preventDefault(); go(); });
  showSelect.addEventListener("change", go);
  modeSelect.addEventListener("change", go);

  const card = (inv: SupplierInvoice) => {
    const st = invoiceStatus(inv.status);
    const due = dueWords(inv, now);
    const discount = discountWords(inv, now);
    const failed = failedChecks(inv);
    const lines = inv.matchResult?.lines ?? [];
    return h("div", { class: "panel decision" },
      h("div", { class: "row" },
        h("div", null,
          h("h2", { class: "subheading" }, inv.supplier?.name ?? "", " · invoice ", h("span", { class: "num" }, inv.invoiceNumber)),
          h("p", { class: "caption" }, inv.invoiceDate ? `Dated ${shortDate(inv.invoiceDate)} · ` : "", "for ",
            inv.vendorPo ? h("a", { class: "num", href: `/vendor-po/${inv.vendorPo}` }, inv.vendorPo)
              : h("span", { class: "num" }, inv.poNumberAsWritten ?? "no PO"),
            inv.poNumberAsWritten && inv.vendorPo && inv.poNumberAsWritten !== inv.vendorPo ? ` (written ${inv.poNumberAsWritten})` : "")),
        h("span", { class: "number-md" }, money(inv.total))),
      h("div", { class: "row-act" }, pill(st.text.toLowerCase(), st.tone),
        due ? pill(due.text.toLowerCase(), due.tone) : h("span", { class: "caption" }, "No due date yet (the supplier's terms aren't set)"),
        discount ? pill(discount.text, discount.tone) : null,
        inv.freight ? h("span", { class: "caption" }, `includes ${money(inv.freight)} freight`) : null),
      failed.length
        ? h("ul", { class: "cards" }, failed.map((f) => h("li", { class: "card" }, h("span", { class: "label" }, f.check), h("span", null, plain(f.message)))))
        : null,
      lines.length
        ? h("div", { class: "scroll" }, h("table", { class: "tbl" },
            h("thead", null, h("tr", null, h("th", { scope: "col" }, "Line"), h("th", { scope: "col", class: "r" }, "EA"),
              h("th", { scope: "col", class: "r" }, "Price"), h("th", { scope: "col", class: "r" }, "Extended"),
              h("th", { scope: "col" }, "Quantity"), h("th", { scope: "col" }, "Price check"))),
            h("tbody", null, lines.map((l) => h("tr", null,
              h("td", { class: "num" }, l.kind === "ITEM" ? String(l.lineNo) : l.kind.toLowerCase()),
              h("td", { class: "num r" }, qty(l.qtyEa, "")), h("td", { class: "num r" }, unitPrice(l.unitCost)),
              h("td", { class: "num r" }, money(l.extended)),
              h("td", null, pill(l.qtyOk ? "ok" : "off", l.qtyOk ? "good" : "late")),
              h("td", null, pill(l.costOk ? "ok" : "off", l.costOk ? "good" : "late")))))))
        : null);
  };

  return h("div", { class: "page" },
    h("div", { class: "stack-tight" },
      h("h1", { class: "title" }, "Supplier invoices"),
      h("p", { class: "lede" }, rows.length
        ? `${rows.length}${r.next_cursor ? "+" : ""} ${show === "ALL" ? "" : show === "PAST_DUE" ? "past due " : "open "}` +
          `${rows.length === 1 ? "invoice" : "invoices"}, ${money(rows.reduce((s, i) => s + i.total, 0))}` +
          `${unmatched ? `; ${unmatched} didn't match` : ""}${pastDue ? `; ${pastDue} past due` : ""}.`
        : "No supplier invoices match."),
      h("p", { class: "caption" }, "Matched to the vendor PO: price exact to 3 decimals per EA, quantity within 10%, extended within $1. " +
        "Paying them stays with you; nothing here moves money.")),
    form,
    view.other ? modeNotice(view.mode, r.mode) : null,
    rows.length ? rows.map(card) : empty("Nothing here", "Try All, or another supplier."),
    r.next_cursor ? h("p", { class: "caption" }, "More than 200: narrow it by supplier.") : null,
    label(view.mode === "SIMULATION" ? "Simulation copies" : "Live"));
}
